import { graphGetJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";

interface MeResponse {
  readonly mail?: string | null;
  readonly userPrincipalName?: string | null;
  /** "SMTP:primary@…" and "smtp:alias@…", plus entries that aren't email addresses ("SIP:", "X500:"). */
  readonly proxyAddresses?: readonly string[] | null;
}

export interface MyAddresses {
  /** The primary address of the account signed in to AiVA: what invites sent from the pane come from. */
  readonly primary: string;
  /** Every address of that mailbox, aliases included, lower-cased. */
  readonly all: readonly string[];
}

/**
 * The addresses of the mailbox you signed in to AiVA with. Outlook's profile
 * gives only one address, and in an Outlook with several accounts not
 * necessarily this one; an invite can also reach you through any alias, and
 * Graph then lists you under that alias. Reading your own profile needs only
 * User.Read.
 */
export async function fetchMyAddresses(token: string, refresh: RefreshTokenFn | undefined): Promise<MyAddresses> {
  const me = await graphGetJson<MeResponse>("/me?$select=mail,userPrincipalName,proxyAddresses", token, refresh);
  const addresses = new Set<string>();
  const add = (value: string | null | undefined) => {
    const address = (value ?? "").trim().toLowerCase();
    if (address.includes("@")) addresses.add(address);
  };
  add(me.mail);
  add(me.userPrincipalName);
  for (const proxy of me.proxyAddresses ?? []) {
    if (/^smtp:/i.test(proxy)) add(proxy.slice("smtp:".length));
  }
  const primary = (me.mail ?? me.userPrincipalName ?? "").trim().toLowerCase();
  return { primary: primary.includes("@") ? primary : "", all: [...addresses] };
}
